import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { useCallPresence } from "@/lib/presence";
import { useAuth } from "@/context/AuthContext";
import DispatchMap from "@/components/DispatchMap";
import { announceStatus, emergencyAlarm, speak, blip, messageBeeps, hazardTone } from "@/lib/audio";
import CloseCallDisposition from "@/components/CloseCallDisposition";
import MDTClearSheet from "@/components/MDTClearSheet";
import ShiftBrief from "@/components/ShiftBrief";
import FieldFormModal from "@/components/FieldForms";
import PanicBanner from "@/components/PanicBanner";
import MDTLockScreen from "@/components/MDTLockScreen";
import ShiftSignOn from "@/pages/ShiftSignOn";
import AgencyDirectory from "@/components/AgencyDirectory";
import { DarPanel } from "@/components/DarPanel";
import { FieldInterview } from "@/components/FieldInterview";
import { toast } from "sonner";
import { TriangleAlert, CheckCircle, LogOut, ExternalLink, WifiOff, Loader2 } from "lucide-react";
import { AssistantWidget } from "@/components/AssistantWidget";
import { getPushEnabled, enablePush, disablePush } from "@/lib/push";
import { t, STATUS_META, NAV, IDLE_LOCK_MS, isUnreadMsg } from "@/components/mdt/constants";
import { MdtTopBar } from "@/components/mdt/MdtTopBar";
import { MdtStatusBar } from "@/components/mdt/MdtStatusBar";
import { MdtActiveCall } from "@/components/mdt/MdtActiveCall";
import { MdtHomePanels } from "@/components/mdt/MdtHomePanels";
import { MdtOnViewCard } from "@/components/mdt/MdtOnViewCard";
import { MdtStatusStrip, MdtSafetyPanel, BoloCenter } from "@/components/mdt/MdtFieldOps";
import { FeedbackQrCard } from "@/components/FeedbackQrCard";
import MdtFieldForms from "@/components/mdt/MdtFieldForms";
import { MdtCallsView } from "@/components/mdt/MdtCallsView";
import { MdtMessagesView } from "@/components/mdt/MdtMessagesView";
import { MdtReportsView } from "@/components/mdt/MdtReportsView";
import { MdtGpsGate } from "@/components/mdt/MdtGpsGate";
import { MdtGpsConsent } from "@/components/mdt/MdtGpsConsent";
import { useLiveGps } from "@/components/mdt/useLiveGps";

export default function MDT() {
  const { user, logout } = useAuth();
  useEffect(() => { userIdRef.current = user?.id || null; }, [user?.id]);
  const nav = useNavigate();
  const [myUnit, setMyUnit] = useState(localStorage.getItem("mdt_unit") || "MOPS-21");
  const [onView, setOnView] = useState(null);
  const [unit, setUnit] = useState(null);
  const [allUnits, setAllUnits] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [view, setView] = useState("home");
  const [signedOn, setSignedOn] = useState(!!localStorage.getItem("mdt_shift"));
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [formType, setFormType] = useState(null);
  const [dispIncident, setDispIncident] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [locked, setLocked] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [reportsRefreshKey, setReportsRefreshKey] = useState(0);
  const prevAssign = useRef(null);
  const prevIncoming = useRef(null);
  const userIdRef = useRef(null);
  const prevFlagged = useRef(null);
  const idleRef = useRef(Date.now());
  // Suppress background reloads while the officer is typing (report / field note /
  // message / log edit) so a poll or WebSocket push can't re-render mid-entry.
  const typingRef = useRef(false);
  const typingT = useRef(null);
  const markTyping = () => { typingRef.current = true; clearTimeout(typingT.current); typingT.current = setTimeout(() => { typingRef.current = false; }, 2500); };

  useEffect(() => { getPushEnabled().then(setPushOn).catch(() => {}); }, []);
  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast("Push alerts OFF for this device"); }
      else { await enablePush(); setPushOn(true); toast.success("Push alerts ON — you'll be notified when dispatched or a P1 call drops nearby"); }
    } catch (e) { toast.error(e?.message || "Could not enable push alerts."); }
    setPushBusy(false);
  };

  const openRmsReport = async (idOrCc) => {
    try {
      const r = await client.post(`/rms-reports/from-incident/${idOrCc}`);
      nav(`/rms-report/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to open incident report"); }
  };

  const load = useCallback(async () => {
    try {
      const [inc, un, msg] = await Promise.all([
        client.get("/incidents", { params: { active: true } }),
        client.get("/units"),
        client.get("/messages", { params: { unit: myUnit } }),
      ]);
      setIncidents(inc.data); setAllUnits(un.data);
      setUnit(un.data.find((x) => x.unit_id === myUnit)); setMessages(msg.data);
      // Offline snapshot — last good copy of the board so the MDT stays readable with no signal.
      try { localStorage.setItem("mdt_snapshot", JSON.stringify({ ts: new Date().toISOString(), incidents: inc.data, units: un.data, messages: msg.data })); } catch { /* quota */ }
      setLastSync(new Date().toISOString()); setApiDown(false);
      const mine = inc.data.filter((i) => (i.assigned_units || []).includes(myUnit)).map((i) => i.cc_number);
      if (prevAssign.current !== null) {
        const fresh = inc.data.find((i) => (i.assigned_units || []).includes(myUnit) && !prevAssign.current.includes(i.cc_number));
        if (fresh) {
          const rpt = fresh.repeat_address;
          speak(`New call for service. ${fresh.call_type_label}. Location, ${fresh.location?.address}.${rpt ? ` Caution. Repeat address. ${rpt.count} calls at this location.` : ""}`);
          if (rpt) { hazardTone(); toast.error(`⟳ REPEAT ADDRESS — ${rpt.count} calls at ${fresh.location?.address}. Use caution.`, { duration: 12000 }); }
          else { toast(`📻 Dispatched: ${fresh.call_type_label} @ ${fresh.location?.address}`, { duration: 8000 }); }
        }
      }
      prevAssign.current = mine;
      // Instant peer alert: a call I'm NOT on just raised a BACKUP REQUESTED flag.
      const flagged = inc.data.filter((i) => i.backup_requested).map((i) => i.cc_number);
      if (prevFlagged.current !== null) {
        const newFlag = inc.data.find((i) => i.backup_requested && !prevFlagged.current.includes(i.cc_number) && !(i.assigned_units || []).includes(myUnit));
        if (newFlag) { emergencyAlarm(); speak(`Backup requested. ${newFlag.call_type_label}. ${newFlag.location?.address}.`); toast.error(`\u{1F6A8} BACKUP NEEDED — ${newFlag.call_type_label} @ ${newFlag.location?.address}`, { duration: 12000 }); }
      }
      prevFlagged.current = flagged;
      // Alert on newly RECEIVED messages — timestamp-based (robust even when the
      // message list is capped) and never beeps for this officer's own traffic.
      const incoming = msg.data.filter((m) => m.from_unit !== myUnit && m.from_user_id !== userIdRef.current);
      const newestTs = incoming.length ? incoming[incoming.length - 1].ts : null;
      if (prevIncoming.current !== null && newestTs && newestTs > prevIncoming.current) {
        const last = incoming[incoming.length - 1];
        if (last?.priority) {
          // URGENT flagged by dispatch — full audible alarm + spoken text + red toast.
          emergencyAlarm();
          speak(`Priority message. ${last.text}`);
          toast.error(`🚨 PRIORITY MESSAGE${last?.from_name ? ` from ${last.from_name}` : ""}: ${last.text}`, { duration: 12000 });
        } else {
          // 4 beeps, then the spoken alert (per policy).
          messageBeeps();
          setTimeout(() => speak(`New message received${last?.from_unit ? ` from ${last.from_unit}` : ""}.`), 600);
          toast(`📨 Message received${last?.from_unit ? ` from ${last.from_unit}` : last?.from_name ? ` from ${last.from_name}` : ""}`, { duration: 5000 });
        }
      }
      if (newestTs || prevIncoming.current === null) prevIncoming.current = newestTs || "";
    } catch (e) {
      // Network failure (no response) → offline: restore cached snapshot if the board is empty.
      if (!e?.response) {
        setApiDown(true);
        if (!snapRef.current) {
          try {
            const s = JSON.parse(localStorage.getItem("mdt_snapshot") || "null");
            if (s) { snapRef.current = s; setIncidents(s.incidents || []); setAllUnits(s.units || []); setUnit((s.units || []).find((x) => x.unit_id === myUnit)); setMessages(s.messages || []); setLastSync(s.ts); }
          } catch { /* noop */ }
        }
      }
    } finally { setLoaded(true); }
  }, [myUnit]);
  const [lastSync, setLastSync] = useState(() => { try { return JSON.parse(localStorage.getItem("mdt_snapshot") || "null")?.ts || null; } catch { return null; } });
  const [apiDown, setApiDown] = useState(false);
  const snapRef = useRef(null);
  useEffect(() => { if (!apiDown) snapRef.current = null; }, [apiDown]);

  useEffect(() => { if (signedOn) load(); const iv = setInterval(() => signedOn && !typingRef.current && load(), 2000); return () => clearInterval(iv); }, [load, signedOn]);
  useRealtime(["incidents", "units", "messages"], () => { if (signedOn && !typingRef.current) load(); });
  // Lock the MDT to the signed-in officer's OWN assigned unit (their call-sign badge);
  // they cannot operate any other unit.
  useEffect(() => { if (user?.badge && user.badge !== myUnit) { setMyUnit(user.badge); localStorage.setItem("mdt_unit", user.badge); prevAssign.current = null; } }, [user?.badge]); // eslint-disable-line
  useEffect(() => {
    const bump = () => { idleRef.current = Date.now(); };
    ["mousemove", "keydown", "click", "touchstart"].forEach((e) => window.addEventListener(e, bump));
    const iv = setInterval(() => { if (!locked && Date.now() - idleRef.current > IDLE_LOCK_MS) setLocked(true); }, 5000);
    return () => { ["mousemove", "keydown", "click", "touchstart"].forEach((e) => window.removeEventListener(e, bump)); clearInterval(iv); };
  }, [locked]);

  // Offline queue — status changes AND messages made with no signal are stored locally
  // (localStorage) and auto-synced IN ORDER the moment connectivity returns.
  const [netOn, setNetOn] = useState(navigator.onLine);
  const [statusQueue, setStatusQueue] = useState(() => { try { return JSON.parse(localStorage.getItem("mdt_status_queue") || "[]"); } catch { return []; } });
  const [syncing, setSyncing] = useState(false);
  const flushingRef = useRef(false);
  const enqueueStatus = (s, label) => {
    setStatusQueue((prev) => {
      const q = [...prev, { kind: "status", status: s, label, ts: new Date().toISOString() }];
      localStorage.setItem("mdt_status_queue", JSON.stringify(q));
      toast(`NO SIGNAL — "${label}" queued (#${q.length}). Auto-syncs on reconnect.`, { duration: 6000 });
      return q;
    });
  };
  const enqueueMessage = (body) => {
    setStatusQueue((prev) => {
      const q = [...prev, { kind: "message", body, label: "Message", ts: new Date().toISOString() }];
      localStorage.setItem("mdt_status_queue", JSON.stringify(q));
      toast(`NO SIGNAL — message queued (#${q.length}). Sends automatically on reconnect.`, { duration: 6000 });
      return q;
    });
  };
  // Field notes & field-ops forms written with no signal — synced to the call on reconnect.
  const enqueueItem = (kind, body, label) => {
    setStatusQueue((prev) => {
      const q = [...prev, { kind, body, label, ts: new Date().toISOString() }];
      localStorage.setItem("mdt_status_queue", JSON.stringify(q));
      toast(`NO SIGNAL — ${label} saved on this MDT (#${q.length}). Syncs to the call automatically on reconnect.`, { duration: 6000 });
      return q;
    });
  };
  const flushQueue = useCallback(async () => {
    if (flushingRef.current) return;
    let q = []; try { q = JSON.parse(localStorage.getItem("mdt_status_queue") || "[]"); } catch { q = []; }
    if (!q.length) return;
    flushingRef.current = true; setSyncing(true);
    let synced = 0;
    while (q.length) {
      try {
        if (q[0].kind === "message") await client.post("/messages", { ...q[0].body, offline_ts: q[0].ts });
        else if (q[0].kind === "field_note") await client.post(`/incidents/${q[0].body.incident_id}/events`, { type: "field_note", message: q[0].body.message, offline_ts: q[0].ts });
        else if (q[0].kind === "field_form") await client.post("/field-forms", { ...q[0].body, offline_ts: q[0].ts });
        else await client.patch(`/units/${myUnit}/status`, { status: q[0].status, offline_ts: q[0].ts });
        synced += 1;
      } catch (e) {
        const st = e.response?.status;
        if (!e.response || st >= 500 || st === 408 || st === 429) break; // offline/transient — keep item, retry later
        // permanent 4xx rejection — drop the item so the queue can't jam
      }
      q = q.slice(1);
      localStorage.setItem("mdt_status_queue", JSON.stringify(q));
    }
    setStatusQueue(q);
    flushingRef.current = false; setSyncing(false);
    if (synced) { toast.success(`RECONNECTED — ${synced} queued item${synced > 1 ? "s" : ""} synced`); load(); }
  }, [myUnit, load]);
  useEffect(() => {
    const on = () => { setNetOn(true); flushQueue(); };
    const off = () => setNetOn(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    const iv = setInterval(() => { if (navigator.onLine) flushQueue(); }, 15000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); clearInterval(iv); };
  }, [flushQueue]);

  const changeStatus = async (s, label) => {
    announceStatus(label);
    if (!navigator.onLine || apiDown) { enqueueStatus(s, label); return; }
    try {
      await client.patch(`/units/${myUnit}/status`, { status: s });
      toast.success(`Status: ${label}`);
      if (s === "onscene" && call) smartApproach(call);
      load();
    } catch (e) {
      if (!e.response) { enqueueStatus(s, label); return; }
      toast.error(e.response?.status === 403 ? "Not authorized to change unit status." : (e.response?.data?.detail || "Could not update status"));
    }
  };
  // CLEAR flow: on an active call, open the one-tap disposition sheet first.
  const requestClear = () => {
    if (call) { setClearOpen(true); return; }
    changeStatus("clear", "Cleared");
  };
  const submitClear = async (code, narrative) => {
    if (!code) { setClearOpen(false); changeStatus("clear", "Cleared"); return; }
    if (!navigator.onLine) { toast.error("Offline — dispositions need connectivity. Use 'Clear WITHOUT disposition' to queue."); return; }
    setClearBusy(true);
    try {
      const r = await client.patch(`/units/${myUnit}/status`, { status: "clear", disposition_code: code, disposition_narrative: narrative });
      announceStatus("Cleared");
      toast.success(`Cleared — disposition ${code} filed`);
      setClearOpen(false);
      if (r.data?.rms_report_id) {
        toast.info(`REPORT REQUIRED — opening RMS draft ${r.data.rms_case}`, { duration: 6000 });
        nav(`/rms-report/${r.data.rms_report_id}`);
        return;
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not clear with disposition"); }
    finally { setClearBusy(false); }
  };
  const panic = async () => {
    emergencyAlarm(); speak("Emergency! Officer needs assistance!", { rate: 1.1 });
    // Always transmit the freshest fix with the activation — raw coordinates preserved.
    const fix = lastFixRef.current;
    try { await client.post("/panic", { unit: myUnit, lat: fix?.lat ?? null, lng: fix?.lng ?? null }); toast.error("🚨 PANIC ACTIVATED — all units & dispatch notified" + (fix ? " · LOCATION SENT" : " · NO GPS FIX — last known position on file")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Panic failed"); }
  };
  const requestBackup = async () => { if (!call) { toast.error("No active call to request backup on"); return; } try { await client.post(`/incidents/${call.id}/request-backup`); emergencyAlarm(); speak("Requesting backup."); toast.error("\u{1F6A8} BACKUP REQUESTED — dispatch & nearby units alerted", { duration: 8000 }); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not request backup"); } };
  const clearBackup = async () => { if (!call) return; try { await client.post(`/incidents/${call.id}/clear-backup`); blip(); toast.success("Backup request cleared"); load(); } catch { toast.error("Could not clear backup"); } };
  const selfBackup = async (id) => { try { await client.post(`/incidents/${id}/backup`); speak("Backup en route."); blip(); toast.success(`${myUnit} self-dispatched as backup — EN ROUTE`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not self-dispatch as backup"); } };
  const smartApproach = async (inc) => {
    try { const lat = inc.location.lat + 0.0004, lng = inc.location.lng + 0.0003; const { data } = await client.post(`/units/${myUnit}/locate`, { lat, lng });
      if (data.smart_arrival?.arrived) { speak(`${myUnit} on scene.`); blip(); toast.success(`SMART ARRIVAL — ${myUnit} on-scene`); } else toast(`Approaching — ${data.smart_arrival?.blocks ?? "?"} blocks out`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update location"); }
  };

  // MDT LIVE LOCATION (additive): explicit permission gate + rich GPS pings.
  // Logic lives in components/mdt/useLiveGps.js (refactor — behavior unchanged).
  const { gpsPerm, selfFix, lastFixRef, consent, allowLocation, refreshLocation,
    enableTracking, disableTracking, endGpsSession } = useLiveGps({ signedOn, myUnit, load });
  // GPS OPTION: officer taps the top-bar GPS control → consent question first.
  const [gpsDialog, setGpsDialog] = useState(null); // null | "enable" | "disable"
  const trackingLive = consent && gpsPerm === "granted";
  const [gpsVal, gpsColor] = !consent ? ["OFF", t.dim]
    : gpsPerm === "denied" ? ["DENIED", t.red]
    : gpsPerm === "granted" ? (selfFix ? ["ACTIVE", t.green] : ["ACQUIRING", t.amber])
    : ["STANDBY", t.amber];
  const onGpsOption = () => setGpsDialog(trackingLive ? "disable" : "enable");

  const doLogout = async () => {
    // End the live tracking session first (audited); unit goes GPS-offline.
    await endGpsSession();
    await logout(); nav("/login");
  };

  const assigned = incidents.filter((i) => (i.assigned_units || []).includes(myUnit));
  const openCfs = incidents.filter((i) => i.status !== "closed");
  const call = assigned[0];
  const etaTarget = call && ["dispatched", "enroute", "acknowledged"].includes(unit?.status) && call.status !== "closed" ? call : null;
  const { viewers: callViewers, typers: callTypers, notifyTyping } = useCallPresence({ cc: call?.cc_number, label: myUnit, active: !!call });
  const bolo = incidents.find((i) => i.bolo) || null;
  const [stLabel, stColor] = unit ? (STATUS_META[unit.status] || ["OFF DUTY", t.dim]) : (loaded ? ["OFF DUTY", t.dim] : ["LOADING", t.dim]);
  const msgUnreadTotal = messages.filter((m) => isUnreadMsg(m, myUnit, user)).length;

  // URGENT ACK — dispatcher-flagged priority messages must be COPY-acknowledged
  // before the banner clears (24h window; system/unit-originated traffic excluded).
  const pendingUrgent = messages.filter((m) => m.priority && !m.from_unit
    // Welfare-check messages (from the SAFETY TIMER system) MUST be COPY-ackable by the
    // unit they target; other system traffic stays excluded (additive rule).
    && (m.welfare_check
      ? m.to_unit === myUnit
      : (m.from_user_id
        && m.from_user_id !== user?.id && m.from_role !== "system"
        && (m.to_unit === myUnit || m.scope === "broadcast")))
    && !(m.acks || []).includes(user?.name)
    && (Date.now() - new Date(m.ts).getTime()) < 86400000);
  const ackUrgent = async (mid) => {
    try { await client.post(`/messages/${mid}/ack`); blip(); toast.success("COPY sent — dispatch notified"); load(); }
    catch { toast.error("Could not acknowledge — retry"); }
  };

  if (!signedOn) return <ShiftSignOn onComplete={(u) => { setMyUnit(u); setSignedOn(true); }} />;
  if (locked) return <MDTLockScreen user={user} unit={myUnit} onResume={() => { idleRef.current = Date.now(); setLocked(false); }} />;

  const navGo = (id) => { if (["persons", "vehicles", "ncic"].includes(id)) { nav("/ncic"); return; } setView(id); };

  return (
    <div data-testid="mdt-root" className="app-viewport w-screen flex flex-col overflow-hidden" style={{ background: t.bg, color: t.text }}>
      {user?.env === "training" && <div data-testid="mdt-training-banner" className="shrink-0 bg-[#F59E0B] text-black font-head font-bold text-[11px] text-center py-1 tracking-wide">TRAINING ENVIRONMENT — SIMULATION ONLY · NOT A LIVE SYSTEM</div>}
      <PanicBanner />
      {/* LOCATION PERMISSION GATE — additive; MDT keeps working if denied. Hidden
          when the officer explicitly opted out via the GPS option (no nagging). */}
      {signedOn && consent && <MdtGpsGate gpsPerm={gpsPerm} allowLocation={allowLocation} />}
      {pendingUrgent.length > 0 && (
        <div data-testid="mdt-urgent-ack-banner" className="shrink-0 border-b-2 divide-y divide-[#7f1d1d]" style={{ background: "#3a0f13", borderColor: t.red }}>
          {pendingUrgent.map((m) => (
            <div key={m.id} className="px-3 py-2 flex items-center gap-3 flex-wrap">
              <TriangleAlert size={20} className="animate-pulse shrink-0" style={{ color: "#f87171" }} />
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[9px] uppercase tracking-wide" style={{ color: "#f87171" }}>PRIORITY — {m.from_name} · {new Date(m.ts).toLocaleTimeString("en-GB", { hour12: false, timeZone: "America/New_York" })} · ACKNOWLEDGEMENT REQUIRED</div>
                <div className="font-head font-bold text-sm text-white break-words">{m.text}</div>
              </div>
              <button data-testid={`mdt-urgent-copy-${m.id}`} onClick={() => ackUrgent(m.id)}
                className="shrink-0 px-4 py-2.5 rounded font-head font-bold text-sm text-white animate-pulse flex items-center gap-1.5" style={{ background: t.red }}>
                <CheckCircle size={15} /> COPY
              </button>
            </div>
          ))}
        </div>
      )}

      <MdtTopBar user={user} myUnit={myUnit} stLabel={stLabel} stColor={stColor}
        netOn={netOn && !apiDown} queuedCount={statusQueue.length} syncing={syncing} lastSync={lastSync} pushOn={pushOn} pushBusy={pushBusy}
        togglePush={togglePush} onBrief={() => setBriefOpen(true)} onLock={() => setLocked(true)} onPanic={panic}
        gpsVal={gpsVal} gpsColor={gpsColor} onGps={onGpsOption} />
      {(!netOn || apiDown) && (
        <div data-testid="mdt-offline-banner" className="shrink-0 px-3 py-1.5 flex items-center gap-3 flex-wrap border-b font-mono text-[11px]" style={{ background: "#3a2a0f", borderColor: t.amber, color: "#FCD34D" }}>
          <WifiOff size={14} className="animate-pulse" />
          <b>OFFLINE MODE</b>
          <span>Showing last synced board{lastSync ? ` from ${new Date(lastSync).toLocaleTimeString("en-GB", { hour12: false }).slice(0, 8)}` : ""}. Status changes &amp; messages are queued ({statusQueue.length}) and will sync automatically when signal returns.</span>
        </div>
      )}
      {netOn && !apiDown && syncing && (
        <div data-testid="mdt-syncing-banner" className="shrink-0 px-3 py-1 flex items-center gap-2 border-b font-mono text-[11px]" style={{ background: "#0f2a3a", borderColor: t.blue, color: "#93C5FD" }}>
          <Loader2 size={13} className="animate-spin" /> RECONNECTED — syncing {statusQueue.length} queued item{statusQueue.length === 1 ? "" : "s"}…
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* LEFT NAV RAIL — desktop / large tablets */}
        <div className="w-[76px] shrink-0 hidden md:flex flex-col py-2 gap-1 overflow-y-auto" style={{ background: t.head, borderRight: `1px solid ${t.border}` }}>
          {NAV.map(([id, label, Icon]) => {
            const on = view === id;
            return (
              <button key={id} data-testid={`mdt-nav-${id}`} onClick={() => navGo(id)} className="relative mx-2 flex flex-col items-center gap-1 py-2.5 rounded-lg transition-colors"
                style={on ? { background: t.blue, color: "#fff" } : { color: t.dim }}><Icon size={19} />
                {id === "messages" && msgUnreadTotal > 0 && <span data-testid="mdt-msg-unread-badge" className="absolute top-0.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-white font-mono text-[9px] font-bold flex items-center justify-center">{msgUnreadTotal > 99 ? "99+" : msgUnreadTotal}</span>}
                <span className="font-mono text-[8px] tracking-wide">{label}</span></button>
            );
          })}
          <button data-testid="mdt-nav-console" onClick={() => nav("/")} className="mx-2 mt-auto flex flex-col items-center gap-1 py-2.5 rounded-lg" style={{ color: t.dim, display: user?.role === "pa_state_constable" ? "none" : undefined }}><ExternalLink size={18} /><span className="font-mono text-[8px]">CONSOLE</span></button>
          <button data-testid="mdt-logout-btn" onClick={doLogout} className="mx-2 flex flex-col items-center gap-1 py-2.5 rounded-lg" style={{ color: t.dim }}><LogOut size={18} /><span className="font-mono text-[8px]">SIGN OFF</span></button>
        </div>

        {/* MAIN */}
        <div className={`flex-1 flex flex-col p-2.5 gap-2.5 overflow-y-auto ${view === "home" ? "" : "md:overflow-hidden"}`}>
          {view === "home" && (
            <>
              <MdtStatusBar unit={unit} stLabel={stLabel} stColor={stColor}
                onAction={(s, label) => (s === "clear" ? requestClear() : changeStatus(s, label))} />
              <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-2.5 shrink-0">
                <MdtActiveCall call={call} myUnit={myUnit} user={user} loaded={loaded} load={load}
                  changeStatus={changeStatus} requestClear={requestClear} openRmsReport={openRmsReport}
                  onCloseCall={setDispIncident} callViewers={callViewers} callTypers={callTypers}
                  notifyTyping={notifyTyping} markTyping={markTyping} offline={!netOn || apiDown} enqueueItem={enqueueItem} />
                {/* MAP — guaranteed height everywhere now that the home view scrolls */}
                <div className="rounded-lg border overflow-hidden min-h-[280px] xl:min-h-[400px]" style={{ borderColor: t.border }}><DispatchMap units={allUnits} incidents={assigned} height="100%" onSelectIncident={(i) => nav(`/incident/${i.id}`)} selfPos={selfFix} onRefreshFix={refreshLocation} etaTarget={etaTarget} /></div>
              </div>
              <MdtHomePanels call={call} bolo={bolo} messages={messages}
                onOpenMessages={() => setView("messages")} onSelfInit={(k) => setOnView(k || "on_view")}
                requestBackup={requestBackup} clearBackup={clearBackup} panic={panic} />
              <MdtOnViewCard open={!!onView} initialKind={onView} myUnit={myUnit} fix={selfFix || lastFixRef.current} onClose={() => setOnView(null)} onCreated={() => load()} onRefreshFix={refreshLocation} />
              <MdtStatusStrip myUnit={myUnit} unit={unit} onChanged={load} offline={!netOn || apiDown} enqueueStatus={enqueueStatus} activeCall={call} />
              <MdtSafetyPanel myUnit={myUnit} activeCc={call?.cc_number} />
              <FeedbackQrCard myUnit={myUnit} activeCc={call?.cc_number} compact />
              <MdtFieldForms myUnit={myUnit} activeCc={call?.cc_number} offline={!netOn || apiDown} enqueueItem={enqueueItem} pendingForms={statusQueue.filter((q) => q.kind === "field_form")} />
              <BoloCenter isSupervisor={["supervisor", "watch_commander", "records_supervisor", "admin", "police_dispatcher", "fire_dispatcher", "ems_dispatcher"].includes(user?.role)} myUnit={myUnit} />
              {call?.premise_alert && <div data-testid="mdt-caution-bar" className="shrink-0 rounded-lg bg-[#DC2626] text-white font-head font-bold text-center py-2.5 flex items-center justify-center gap-2"><TriangleAlert size={18} /> CAUTION: {call.premise_alert.summary}</div>}
            </>
          )}

          {/* MAP (full operational picture) */}
          {view === "map" && (
            <>
              <MdtStatusBar unit={unit} stLabel={stLabel} stColor={stColor} testidPrefix="mdt-map-status"
                onAction={(s, label) => changeStatus(s, label)} />
              <div className="flex-1 rounded-lg border overflow-hidden min-h-[320px]" style={{ borderColor: t.border }} data-testid="mdt-map-view">
                <DispatchMap units={allUnits} incidents={openCfs} height="100%" onSelectIncident={(i) => nav(`/incident/${i.id}`)} enableCoverage selfPos={selfFix} onRefreshFix={refreshLocation} etaTarget={etaTarget} />
              </div>
            </>
          )}

          {view === "calls" && <MdtCallsView incidents={incidents} myUnit={myUnit} user={user} selfBackup={selfBackup} />}
          {view === "messages" && <MdtMessagesView messages={messages} allUnits={allUnits} myUnit={myUnit} user={user} load={load} markTyping={markTyping} offline={!netOn || apiDown} enqueueMessage={enqueueMessage} />}
          {view === "reports" && <MdtReportsView call={call} myUnit={myUnit} openRmsReport={openRmsReport} onOpenForm={setFormType} markTyping={markTyping} refreshKey={reportsRefreshKey} />}
          {view === "fi" && <FieldInterview />}
          {view === "dar" && <DarPanel t={t} myUnit={myUnit} />}
          {view === "directory" && (
            <div className="flex-1 overflow-hidden rounded-lg border min-h-[320px]" style={{ borderColor: t.border }} data-testid="mdt-directory-view">
              <AgencyDirectory />
            </div>
          )}
        </div>
      </div>

      {/* BOTTOM NAV — phones & small tablets (touch-first, horizontally scrollable) */}
      <div data-testid="mdt-bottom-nav" className="md:hidden shrink-0 flex items-stretch overflow-x-auto border-t"
        style={{ background: t.head, borderColor: t.border, paddingBottom: "env(safe-area-inset-bottom)" }}>
        <button data-testid="mdt-bnav-emergency" onClick={panic}
          className="flex flex-col items-center justify-center gap-0.5 px-3 py-2 min-w-[64px] shrink-0 text-white"
          style={{ background: t.red }}>
          <TriangleAlert size={18} /><span className="font-mono text-[8px] font-bold">PANIC</span>
        </button>
        {NAV.filter(([id]) => !["persons", "vehicles"].includes(id)).map(([id, label, Icon]) => {
          const on = view === id;
          return (
            <button key={id} data-testid={`mdt-bnav-${id}`} onClick={() => navGo(id)}
              className="relative flex flex-col items-center justify-center gap-0.5 px-3 py-2 min-w-[64px] shrink-0"
              style={on ? { color: "#fff", background: t.blue } : { color: t.dim }}>
              <Icon size={18} />
              {id === "messages" && msgUnreadTotal > 0 && <span className="absolute top-1 right-2 min-w-[15px] h-4 px-1 rounded-full bg-[#DC2626] text-white font-mono text-[9px] font-bold flex items-center justify-center">{msgUnreadTotal > 99 ? "99+" : msgUnreadTotal}</span>}
              <span className="font-mono text-[8px] tracking-wide whitespace-nowrap">{label}</span>
            </button>
          );
        })}
        <button data-testid="mdt-bnav-logout" onClick={doLogout} className="flex flex-col items-center justify-center gap-0.5 px-3 py-2 min-w-[64px] shrink-0" style={{ color: t.dim }}>
          <LogOut size={18} /><span className="font-mono text-[8px]">SIGN OFF</span>
        </button>
      </div>

      <FieldFormModal type={formType} open={!!formType} onClose={() => setFormType(null)} incidentCc={call?.cc_number || null} unit={myUnit} onSubmitted={() => setReportsRefreshKey((k) => k + 1)} />
      <CloseCallDisposition open={!!dispIncident} onClose={() => setDispIncident(null)} incidentId={dispIncident?.id} incident={dispIncident} onDone={() => { setDispIncident(null); load(); }} />
      <MDTClearSheet open={clearOpen} onClose={() => setClearOpen(false)} onSubmit={submitClear} busy={clearBusy} />
      <ShiftBrief open={briefOpen} onClose={() => setBriefOpen(false)} />
      <MdtGpsConsent mode={gpsDialog} onClose={() => setGpsDialog(null)}
        onAllow={enableTracking} onDisable={disableTracking} />
      <AssistantWidget />
    </div>
  );
}
